import type { AuthUser, LoginResponse, MfaChallengeResponse } from '@school-erp/types';
import { useQueryClient } from '@tanstack/react-query';
import {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { api, refreshSession, setAccessToken, setSessionExpiredHandler } from '../api/http';

type Status = 'loading' | 'anonymous' | 'authenticated';

interface AuthState {
  status: Status;
  user: AuthUser | null;
  /** True after the person chose to sign out (as opposed to the session running out). */
  signedOutOnPurpose: boolean;
  /** Resolves to null once signed in, or to the challenge to answer when the account has a second step. */
  login(identifier: string, password: string): Promise<{ mfaToken: string } | null>;
  /** The second step: the code from the authenticator app, or a recovery code. */
  verifyMfa(mfaToken: string, proof: { code?: string; recoveryCode?: string }): Promise<void>;
  logout(): Promise<void>;
  /** UI convenience only — the API enforces every permission independently. */
  can(permission: string, schoolId?: string): boolean;
  /** Holds the permission organization-wide or for at least one school. */
  canAny(permission: string): boolean;
}

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<Status>('loading');
  const [user, setUser] = useState<AuthUser | null>(null);
  const [signedOutOnPurpose, setSignedOutOnPurpose] = useState(false);
  const queryClient = useQueryClient();

  const clear = useCallback(() => {
    setAccessToken(null);
    setUser(null);
    setStatus('anonymous');
    queryClient.clear();
  }, [queryClient]);

  // Restore the session from the refresh cookie on first load.
  useEffect(() => {
    let cancelled = false;
    void refreshSession().then((session) => {
      if (cancelled) return;
      if (session) {
        setUser(session.user);
        setStatus('authenticated');
      } else {
        setStatus('anonymous');
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    setSessionExpiredHandler(clear);
    return () => setSessionExpiredHandler(null);
  }, [clear]);

  const start = useCallback((session: LoginResponse) => {
    setAccessToken(session.accessToken);
    setUser(session.user);
    setStatus('authenticated');
    setSignedOutOnPurpose(false);
  }, []);

  const login = useCallback(
    async (identifier: string, password: string) => {
      const result = await api<LoginResponse | MfaChallengeResponse>('/auth/login', {
        method: 'POST',
        body: { identifier, password },
        anonymous: true,
      });
      if ('mfaRequired' in result) return { mfaToken: result.mfaToken };
      start(result);
      return null;
    },
    [start],
  );

  const verifyMfa = useCallback(
    async (mfaToken: string, proof: { code?: string; recoveryCode?: string }) => {
      start(
        await api<LoginResponse>('/auth/mfa/verify', {
          method: 'POST',
          body: { mfaToken, ...proof },
          anonymous: true,
        }),
      );
    },
    [start],
  );

  const logout = useCallback(async () => {
    try {
      await api('/auth/logout', { method: 'POST', anonymous: true });
    } finally {
      setSignedOutOnPurpose(true);
      clear();
    }
  }, [clear]);

  const value = useMemo<AuthState>(
    () => ({
      status,
      user,
      signedOutOnPurpose,
      login,
      verifyMfa,
      logout,
      can: (permission, schoolId) =>
        !!user &&
        (user.isSuperAdmin ||
          user.permissions.includes(permission) ||
          (!!schoolId && !!user.schoolPermissions[schoolId]?.includes(permission))),
      canAny: (permission) =>
        !!user &&
        (user.isSuperAdmin ||
          user.permissions.includes(permission) ||
          Object.values(user.schoolPermissions).some((codes) => codes.includes(permission))),
    }),
    [status, user, signedOutOnPurpose, login, verifyMfa, logout],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used within AuthProvider');
  return context;
}
